import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonCard } from '../../components/ui/Skeleton';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { fmtEur, fmtDate } from '../../lib/utils';
import {
  cashForecastQuery, monthStart, monthEnd, monthShort, daysFromToday, plurale, TAX_SOURCE_LABEL,
  type AdminOverview, type AdminAccount,
} from './adminApi';
import { useAdminActions } from './useAdminActions';

/**
 * Panoramica — griglia a card (bento come in Oggi):
 *   Questo mese · Prossimi 3 mesi · Prossime scadenze fiscali · Da controllare · Conti
 */
export function PanoramicaTab({ ov, loading, canWrite, onTab, showHistory }: {
  ov: AdminOverview | undefined;
  loading: boolean;
  canWrite: boolean;
  onTab: (t: 'incassi' | 'uscite' | 'fisco' | 'movimenti' | 'riconciliazione') => void;
  showHistory?: boolean;
}) {
  if (loading && !ov) {
    return (
      <div className="adm-bento">
        <SkeletonCard titleW={160} blocks={2} />
        <SkeletonCard titleW={200} blocks={3} />
        <SkeletonCard titleW={180} blocks={2} />
        <SkeletonCard titleW={140} blocks={1} />
      </div>
    );
  }
  if (!ov) {
    return <EmptyState brand title="Amministrazione non ancora pronta" description="L'API non risponde: riprova tra un momento." />;
  }
  return (
    <div className="adm-bento">
      <ThisMonthCard ov={ov} onTab={onTab} />
      <ForecastCard showHistory={showHistory} />
      <TaxesCard ov={ov} onTab={onTab} />
      <ToReviewCard ov={ov} canWrite={canWrite} onTab={onTab} />
      <AccountsCard accounts={ov.accounts} canWrite={canWrite} />
    </div>
  );
}

// ── Questo mese: quadro 2×2 ────────────────────────────────────────────────
function ThisMonthCard({ ov, onTab }: { ov: AdminOverview; onTab: (t: 'incassi' | 'uscite') => void }) {
  const inc = ov.incassi.thisMonth;
  const out = ov.uscite.thisMonth;
  const monthName = new Date().toLocaleDateString('it-IT', { month: 'long' });
  return (
    <div className="card">
      <div className="card-hd">
        <div className="flex items-center gap-3">
          <div className="icon-tile sm"><Icon name="calendar" size={14} /></div>
          <div>
            <div className="t-h3">Questo mese</div>
            <div className="mute t-small">{monthName.charAt(0).toUpperCase() + monthName.slice(1)} · netto atteso € {fmtEur(inc.expected - out.expected)}</div>
          </div>
        </div>
      </div>
      <div className="quadro">
        <div className="quadro-cell is-click" role="button" tabIndex={0} onClick={() => onTab('incassi')} onKeyDown={(e) => { if (e.key === 'Enter') onTab('incassi'); }}>
          <div className="quadro-label">Incassi attesi</div>
          <div className="quadro-value"><span className="u">€</span>{fmtEur(inc.expected)}</div>
          <div className="quadro-foot">ricevuti € {fmtEur(inc.received)}</div>
        </div>
        {/* "Da incassare" nudo è già la stat-band in alto (tutto l'aperto): qui si
            dice solo la fetta del mese, altrimenti sono due numeri diversi con
            la stessa etichetta nella stessa schermata. */}
        <div className="quadro-cell is-click" role="button" tabIndex={0} onClick={() => onTab('incassi')} onKeyDown={(e) => { if (e.key === 'Enter') onTab('incassi'); }}>
          <div className="quadro-label">In scadenza entro fine mese</div>
          <div className="quadro-value"><span className="u">€</span>{fmtEur(inc.open)}</div>
          <div className={`quadro-foot${inc.overdue > 0 ? ' text-alert' : ''}`}>{inc.overdue > 0 ? `€ ${fmtEur(inc.overdue)} già scaduti` : 'niente in ritardo'}</div>
        </div>
        <div className="quadro-cell is-click" role="button" tabIndex={0} onClick={() => onTab('uscite')} onKeyDown={(e) => { if (e.key === 'Enter') onTab('uscite'); }}>
          <div className="quadro-label">Uscite attese</div>
          <div className="quadro-value"><span className="u">€</span>{fmtEur(out.expected)}</div>
          <div className="quadro-foot">pagate € {fmtEur(out.paid)}</div>
        </div>
        <div className="quadro-cell is-click" role="button" tabIndex={0} onClick={() => onTab('uscite')} onKeyDown={(e) => { if (e.key === 'Enter') onTab('uscite'); }}>
          <div className="quadro-label">Da pagare entro fine mese</div>
          <div className="quadro-value"><span className="u">€</span>{fmtEur(out.open)}</div>
          {/* open include lo scaduto: si dice quanto è arretrato e quanto scade davvero nel mese */}
          <div className={`quadro-foot${(out.overdue ?? 0) > 0 ? ' text-alert' : ''}`}>
            {(out.overdue ?? 0) > 0 ? `€ ${fmtEur(out.overdue)} scaduti · € ${fmtEur(out.open - (out.overdue ?? 0))} entro fine mese` : `mese prossimo € ${fmtEur(ov.uscite.nextMonth.expected)}`}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Prossimi 3 mesi: tabella dai bucket del forecast (solo numeri: nella card
//    a mezza pagina le barre rubavano spazio alle cifre) ──────────────────────
function ForecastCard({ showHistory }: { showHistory?: boolean }) {
  const from = monthStart(0);
  const to = monthEnd(2);
  const { data, isLoading } = useQuery(cashForecastQuery(from, to, showHistory));
  const buckets = data?.buckets ?? [];
  return (
    <div className="card">
      <div className="card-hd">
        <div className="flex items-center gap-3">
          <div className="icon-tile sm"><Icon name="chart" size={14} /></div>
          <div>
            <div className="t-h3">Prossimi 3 mesi</div>
            <div className="mute t-small">Fatture, ricorrenti e tasse già note · saldo di partenza € {fmtEur(data?.start ?? 0)}</div>
          </div>
        </div>
      </div>
      {isLoading && !data ? (
        <div className="card-bd skeleton-col"><span className="skeleton sk-block" /><span className="skeleton sk-block" /></div>
      ) : buckets.length === 0 ? (
        <div className="card-bd"><EmptyState icon="chart" title="Nessuna previsione" description="Quando ci sono scadenze nei prossimi mesi, compaiono qui." /></div>
      ) : (
        /* Cinque colonne di cifre che si leggono solo confrontandole fra loro
           (entrate contro uscite, netto contro saldo): a schede diventerebbero
           tre blocchi separati e il confronto sparirebbe. È il caso di
           mobile.md §8 — si dice che si fa dal computer invece di mostrarla
           male. Prima si schiacciava dentro 356px e sbordava di 121. */
        <DesktopOnly
          cosa="La previsione dei prossimi tre mesi"
          perche="sono cinque colonne di cifre che hanno senso solo confrontate fra loro, e su uno schermo stretto il confronto si perde."
        >
        <table className="tbl flat-head adm-forecast">
          <thead>
            <tr>
              <th>Mese</th>
              <th className="num">Entrate</th>
              <th className="num">Uscite</th>
              <th className="num">Netto</th>
              <th className="num">Saldo</th>
            </tr>
          </thead>
          <tbody>
            {buckets.map((b) => (
              <tr key={b.month} style={{ cursor: 'default' }}>
                <td className="font-semibold">{monthShort(b.month)}</td>
                <td className="num mono">€ {fmtEur(b.in.total)}</td>
                <td className="num mono">€ {fmtEur(b.out.total)}</td>
                <td className={`num mono font-semibold ${b.net < 0 ? 'text-alert' : ''}`}>{b.net < 0 ? '−' : '+'} € {fmtEur(Math.abs(b.net))}</td>
                {/* Il segno sta PRIMA dell'euro, come in Movimenti e come nella
                    colonna qui accanto. Scritto `fmtEur(b.cumulative)` il meno
                    finiva dentro il numero ("€ -74.927") e per giunta col
                    trattino di Intl invece del meno tipografico: due formati
                    per la stessa cosa, affiancati nella stessa tabella. Il
                    saldo però non porta il "+" quando è positivo: è una
                    giacenza, non una variazione. */}
                <td className={`num mono font-semibold ${b.cumulative < 0 ? 'text-alert' : ''}`}>{b.cumulative < 0 ? '− ' : ''}€ {fmtEur(Math.abs(b.cumulative))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </DesktopOnly>
      )}
    </div>
  );
}

// ── Prossime scadenze fiscali ──────────────────────────────────────────────
function TaxesCard({ ov, onTab }: { ov: AdminOverview; onTab: (t: 'fisco') => void }) {
  const next = ov.tasse.next ?? [];
  return (
    <div className="card">
      <div className="card-hd">
        <div className="flex items-center gap-3">
          <div className="icon-tile sm"><Icon name="calendarClock" size={14} /></div>
          <div>
            <div className="t-h3">Prossime scadenze fiscali</div>
            <div className="mute t-small">Aperto in totale € {fmtEur(ov.tasse.openTotal)}</div>
          </div>
        </div>
        {/* 10 come ogni altra icona dentro un `btn-sm` dell'Amministrazione:
            il gemello di questo bottone ("Vedi tutte", nella card accanto) era
            già a 10 e i due chevron non erano uguali. */}
        <button className="btn btn-sm btn-ghost" onClick={() => onTab('fisco')}>Fisco <Icon name="chevronR" size={10} /></button>
      </div>
      <div className="card-bd card-bd--tight">
        {next.length === 0 ? (
          <EmptyState icon="check" title="Nessuna scadenza in vista" description="Le scadenze fiscali registrate compaiono qui." />
        ) : next.slice(0, 6).map((t, i) => {
          const days = daysFromToday(t.dueDate);
          const overdue = days != null && days < 0;
          const soon = days != null && days >= 0 && days <= 7;
          return (
            <div key={t.installmentId ?? `${t.kind}-${t.dueDate}-${i}`} className="adm-line">
              <div className={`mono text-[12px] ${overdue ? 'text-alert' : soon ? 'text-warn' : 'mute'}`} style={{ width: 84 }}>{fmtDate(t.dueDate)}</div>
              <div className="min-w-0 flex-1 truncate">
                {t.description}
                {t.entity === 'PERSONALE_ANDREA' && <span className="chip ghost adm-mini">Personale</span>}
              </div>
              <Chip tone={t.source === 'STIMA' ? 'warn' : 'ghost'}>{TAX_SOURCE_LABEL[t.source] ?? t.source}</Chip>
              <div className="mono font-semibold text-right" style={{ width: 96 }}>€ {fmtEur(t.amount)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Da controllare: rata + movimento Qonto compatibile ─────────────────────
/**
 * Anteprima della riconciliazione: le rate aperte per cui in banca c'è un
 * movimento che le spiega senza margine di dubbio.
 *
 * Qui arrivano SOLO le proposte certe (l'API filtra: vedi admin.reports.ts).
 * Le dubbie stanno nella tab "Da riconciliare", dove accanto alla riga ci sono
 * il movimento, la data e il nome sul bonifico, e c'è anche il pulsante per
 * scartarla. Decidere su una rata dubbia da qui, con due righe di contesto e un
 * clic, è esattamente il modo in cui si assegna il bonifico di un cliente alla
 * fattura di un altro.
 */
function ToReviewCard({ ov, canWrite, onTab }: { ov: AdminOverview; canWrite: boolean; onTab: (t: 'riconciliazione') => void }) {
  const { markPaid } = useAdminActions();
  const items = ov.fatture.toReview ?? [];
  const ric = ov.fatture.daRiconciliare;
  // Proposte che questa card non mostra perché vogliono un occhio, non un clic
  const daGuardare = Math.max(0, (ric?.proposte ?? 0) - (ric?.certe ?? 0));
  // Quale riga sta chiedendo conferma: una sola per volta
  const [confermo, setConfermo] = useState<string | null>(null);
  return (
    <div className="card">
      <div className="card-hd">
        <div className="flex items-center gap-3">
          <div className="icon-tile sm" style={{ ['--c' as any]: items.length ? 'var(--accent)' : undefined }}><Icon name="swap" size={14} /></div>
          <div>
            <div className="t-h3">Da controllare</div>
            <div className="mute t-small">
              {items.length
                ? `${plurale(items.length, 'rata', 'rate')} con il bonifico giusto dietro${daGuardare ? `, e ${daGuardare} da guardare` : ''}`
                : daGuardare
                  ? `${plurale(daGuardare, 'proposta', 'proposte')} da guardare: nessuna abbastanza sicura da chiudersi da sola`
                  : 'Rate con un movimento Qonto compatibile'}
            </div>
          </div>
        </div>
        {(items.length > 0 || daGuardare > 0) && (
          <button className="btn btn-sm btn-ghost" title="Apri la riconciliazione completa, uscite comprese" onClick={() => onTab('riconciliazione')}>
            Vedi tutte <Icon name="chevronR" size={10} />
          </button>
        )}
      </div>
      <div className="card-bd card-bd--tight">
        {items.length === 0 ? (
          daGuardare > 0 ? (
            <EmptyState
              icon="help"
              title="Niente da chiudere con un clic"
              description={`${daGuardare === 1 ? 'Resta' : 'Restano'} ${plurale(daGuardare, 'proposta', 'proposte')} dove il bonifico da solo non basta a dire quale rata ha pagato. ${daGuardare === 1 ? 'Aprila' : 'Aprile'} in "Da riconciliare": lì c'è la prova accanto.`}
            />
          ) : (
            <EmptyState icon="check" title="Niente da controllare" description="Quando un bonifico somiglia a una rata aperta, te lo proponiamo qui." />
          )
        ) : items.slice(0, 8).map((r) => (
          <div key={r.paymentId} className="adm-line">
            <div className="min-w-0 flex-1">
              <div className="truncate">
                <strong>{r.clientName}</strong><span className="mute"> · {r.invoiceNumber}</span>
                {r.certezza && r.certezza !== 'CERTO' && (
                  <Chip tone={r.certezza === 'PROBABILE' ? 'warn' : 'info'} className="ml-2">{r.certezza.toLowerCase()}</Chip>
                )}
              </div>
              <div className="text-[12px] mute truncate" title={r.motivo}>bonifico del {fmtDate(r.transaction.settledOn)} · {r.transaction.counterparty || r.transaction.label}</div>
            </div>
            <div className="mono font-semibold text-right" style={{ width: 96 }}>€ {fmtEur(r.amount)}</div>
            {/* Solo le proposte certe si possono chiudere da qui: le altre vogliono
                la prova sotto gli occhi, e quella sta nella tab dedicata. */}
            {canWrite && r.certezza && r.certezza !== 'CERTO' ? (
              <button className="btn btn-sm btn-ghost shrink-0" title="Guarda la prova prima di decidere" onClick={() => onTab('riconciliazione')}>
                <Icon name="eye" size={10} /> Guarda
              </button>
            ) : canWrite && (
              /* Due passaggi, non uno. Registrare un incasso è una scrittura sui
                 soldi e un clic per sbaglio la faceva partire senza dire cosa
                 stava per succedere: è capitato davvero il 20/09/2026. Adesso il
                 primo clic mostra cosa si sta per registrare e chiede conferma. */
              confermo === r.paymentId ? (
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-[12px] mute">Registro € {fmtEur(r.amount)}?</span>
                  <button
                    className="btn btn-sm btn-primary"
                    disabled={markPaid.isPending}
                    onClick={() => {
                      setConfermo(null);
                      markPaid.mutate({
                        paymentId: r.paymentId,
                        amount: r.amount,
                        label: `${r.invoiceNumber} · ${r.clientName}`,
                        paidDate: r.transaction.settledOn,
                        method: 'BONIFICO',
                        bankTransactionId: r.transaction.id,
                      });
                    }}
                  >
                    Sì, incassata
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={() => setConfermo(null)}>Annulla</button>
                </div>
              ) : (
                <button
                  className="btn btn-sm shrink-0"
                  disabled={markPaid.isPending}
                  title="Chiede conferma prima di registrare"
                  onClick={() => setConfermo(r.paymentId)}
                >
                  <Icon name="check" size={10} /> Segna incassata
                </button>
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Conti: saldo + ultimo aggiornamento + Aggiorna ─────────────────────────
function AccountsCard({ accounts, canWrite }: { accounts: AdminAccount[]; canWrite: boolean }) {
  const { syncQonto, patch } = useAdminActions();
  const [editing, setEditing] = useState<string | null>(null);
  const [value, setValue] = useState('');
  const hasQonto = accounts.some((a) => a.provider === 'QONTO');

  const startEdit = (a: AdminAccount) => { setEditing(a.id); setValue(String(a.balance)); };
  const save = (a: AdminAccount) => {
    const n = Number(value.replace(',', '.'));
    if (!Number.isFinite(n)) return;
    patch.mutate({ path: `/admin/accounts/${a.id}`, data: { openingBalance: n }, okTitle: 'Cassa aggiornata' });
    setEditing(null);
  };

  return (
    <div className="card">
      <div className="card-hd">
        <div className="flex items-center gap-3">
          <div className="icon-tile sm"><Icon name="wallet" size={14} /></div>
          <div className="t-h3">Conti</div>
        </div>
        {canWrite && hasQonto && (
          <button className="btn btn-sm" disabled={syncQonto.isPending} onClick={() => syncQonto.mutate()}>
            <Icon name="refresh" size={10} /> {syncQonto.isPending ? 'Aggiorno…' : 'Aggiorna'}
          </button>
        )}
      </div>
      <div className="card-bd card-bd--tight">
        {accounts.length === 0 ? (
          <EmptyState icon="landmark" title="Nessun conto" description="Collega Qonto dalle Impostazioni: saldi e movimenti arrivano da soli." />
        ) : accounts.map((a) => (
          <div key={a.id} className="adm-line">
            <div className="icon-tile sm"><Icon name={a.provider === 'CASH' ? 'banknote' : 'landmark'} size={14} /></div>
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold">{a.name}</div>
              <div className="text-[12px] mute">{a.balanceAt ? `aggiornato ${fmtDate(a.balanceAt)}` : a.provider === 'CASH' ? 'cassa contanti' : 'mai aggiornato'}</div>
            </div>
            {editing === a.id ? (
              <div className="flex items-center gap-1">
                <input className="inp inp-sm mono" style={{ width: 120 }} autoFocus value={value} onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') save(a); if (e.key === 'Escape') setEditing(null); }} />
                <button className="btn btn-sm btn-primary" onClick={() => save(a)}><Icon name="check" size={10} /></button>
              </div>
            ) : (
              <>
                <div className="mono font-semibold text-right" style={{ width: 110 }}>€ {fmtEur(a.balance, true)}</div>
                {canWrite && a.provider === 'CASH' && (
                  <button className="btn btn-sm btn-ghost btn-icon" title="Correggi il saldo di cassa" aria-label="Correggi saldo" onClick={() => startEdit(a)}>
                    <Icon name="pencil" size={12} />
                  </button>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
